// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from 'react';

import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';

import { DeploymentState, ToolsContent, WizardState } from '../interfaces';
import { TOOLS_CONTENT } from '../steps-config';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Input from '@cloudscape-design/components/input';

import { InfoLink } from '../../commons/common-components';
import Slider from '@cloudscape-design/components/slider';

const { deploy: deployToolsContent } = TOOLS_CONTENT;

interface InstanceOptionsProps extends DeploymentState {
  setHelpPanelContent?: (tools: ToolsContent) => void;
  onChange: (state: Partial<DeploymentState>) => void;
}

interface DeploymentDetails extends DeploymentState {
  setHelpPanelContent?: (tools: ToolsContent) => void;
  onDetailsChange: (state: Partial<DeploymentState>) => void;
}

export const DeploymentDetails = ({
  containers_name,
  containers_image,
  containers_tag,
  resources_requests_memory,
  resources_requests_cpu,
  resources_limits_memory,
  resources_limits_cpu,
  parallelism,
  number_of_workers,
  entry_point,
  onChange,
  setHelpPanelContent,
}: InstanceOptionsProps) => {
  const validateStorageValue = (value: number, range: [number, number]) => {
    if (value === undefined) {
      return '';
    }
    if (value < range[0] || value > range[1]) {
      return `Enter a valid value greater than ${range[0]}) and less than ${range[1]}.`;
    }
    return '';
  };

  return (
    <Container header={<Header variant="h2">Job details</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Name"
          info={<InfoLink onFollow={() => setHelpPanelContent?.(deployToolsContent.default)} />}
          // description="A string that defines the name for the beam job to be deployed into the flink cluster."
          // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
        >
          <Input placeholder="example-name-for-the-container" value={containers_name} onChange={event => onChange({ containers_name: event.detail.value })} />
        </FormField>
        <FormField
          label="Image"
          info={<InfoLink onFollow={() => setHelpPanelContent?.(deployToolsContent.default)} />}
          // description="A string that identifies the image of the beam job to be pulled into the flink cluster."
          // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
        >
          <Input placeholder="example-image-name-for-the-container" value={containers_image} onChange={event => onChange({ containers_image: event.detail.value })} />
        </FormField>
        <FormField
          label="Tag"
          info={<InfoLink onFollow={() => setHelpPanelContent?.(deployToolsContent.default)} />}
          // description="A string that identifies the tag of the beam job to be pulled into the flink cluster."
          // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
        >
          <Input placeholder="example-tag" value={containers_tag} onChange={event => onChange({ containers_tag: event.detail.value })} />
        </FormField>
        <FormField
          label="Entry point"
          info={<InfoLink onFollow={() => setHelpPanelContent?.(deployToolsContent.default)} />}
          // description="A string that identifies the entry point of the job within the container."
          // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
        >
          <Input placeholder="example-tag" value={entry_point} onChange={event => onChange({ entry_point: event.detail.value })} />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory allocated to the job"
            // description="Higher allocated storage may improve IOPS performance."
            errorText={validateStorageValue(Number(resources_requests_memory), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_requests_cpu)}
                  value={Number.isNaN(Number(resources_requests_memory)) ? 1 : Number(resources_requests_memory)}
                  onChange={event => onChange({ resources_requests_memory: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="resources_requests_memory"
                    value={resources_requests_memory}
                    onChange={event => onChange({ resources_requests_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU allocated to the job"
            // description="More CPU to improve performance."
            errorText={validateStorageValue(Number(resources_requests_cpu), [1, 32])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_limits_cpu)}
                  value={Number.isNaN(Number(resources_requests_cpu)) ? 1 : Number(resources_requests_cpu)}
                  onChange={event => onChange({ resources_requests_cpu: String(event.detail.value) })}
                  min={1}
                  max={32}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="resources_requests_cpu"
                    value={resources_requests_cpu}
                    onChange={event => onChange({ resources_requests_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <FormField
            label="Memory limits applied to the job."
            // description="Upperbound of memory made available to the task manager."
            errorText={validateStorageValue(Number(resources_limits_memory), [1, 80])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_limits_memory)}
                  value={Number.isNaN(Number(resources_limits_memory)) ? 1 : Number(resources_limits_memory)}
                  onChange={event => onChange({ resources_limits_memory: String(event.detail.value) })}
                  min={1}
                  max={80}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="resources_limits_memory"
                    value={resources_limits_memory}
                    onChange={event => onChange({ resources_limits_memory: String(event.detail.value) })}
                  />
                </div>
                <div>GiB</div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="CPU limits applied to the job."
            // description="Upperbound of CPU made available to the task manager."
            errorText={validateStorageValue(Number(resources_limits_cpu), [1, 64])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_limits_cpu)}
                  value={Number.isNaN(Number(resources_limits_cpu)) ? 1 : Number(resources_limits_cpu)}
                  onChange={event => onChange({ resources_limits_cpu: String(event.detail.value) })}
                  min={1}
                  max={64}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="resources_limits_cpu"
                    value={resources_limits_cpu}
                    onChange={event => onChange({ resources_limits_cpu: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <FormField
            label="Parallelism."
            // description="Upperbound of memory made available to the task manager."
            errorText={validateStorageValue(Number(parallelism), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_limits_memory)}
                  value={Number.isNaN(Number(parallelism)) ? 1 : Number(parallelism)}
                  onChange={event => onChange({ parallelism: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="parallelism"
                    value={parallelism}
                    onChange={event => onChange({ parallelism: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
          <FormField
            label="Number of workers."
            // description="Upperbound of workers made available to the job."
            errorText={validateStorageValue(Number(number_of_workers), [1, 100])}
          >
            <div className="flex-wrapper">
              <div className="slider-wrapper">
                <Slider
                  ariaLabel="Allocated storage slider"
                  // value={Number(resources_limits_cpu)}
                  value={Number.isNaN(Number(number_of_workers)) ? 1 : Number(number_of_workers)}
                  onChange={event => onChange({ number_of_workers: String(event.detail.value) })}
                  min={1}
                  max={100}
                />
              </div>
              <SpaceBetween size="m" alignItems="center" direction="horizontal">
                <div className="input-wrapper">
                  <Input
                    type="number"
                    autoComplete={true}
                    controlId="number_of_workers"
                    value={number_of_workers}
                    onChange={event => onChange({ number_of_workers: String(event.detail.value) })}
                  />
                </div>
                <div></div>
              </SpaceBetween>
            </div>
          </FormField>
        </ColumnLayout>
        </SpaceBetween>
    </Container>
  );
};
interface DeploymentProps {
  info: WizardState;
  setHelpPanelContent?: (tools: ToolsContent) => void;
  onChange: (state: Partial<DeploymentState>) => void;
}

const Deployment = ({ info: { deployment }, setHelpPanelContent, onChange }: DeploymentProps) => {
  const childProps = { ...deployment, setHelpPanelContent, onChange };
  return (
    <Box margin={{ bottom: 'l' }}>
      <SpaceBetween size="l">
        <DeploymentDetails {...childProps} />
      </SpaceBetween>
    </Box>
  );
};
export default Deployment;